'use client';

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { PrismicNextImage } from '@prismicio/next';
import Link from 'next/link';
import { ConditionalLink } from '@/app/lib/ConditionalLink';
import { Settings } from '@/app/lib/settings';
import { isFilled } from '@prismicio/helpers';
import { RichText } from '@/app/lib/RichText';
import { dashify, formatEventStartDay, formatEventTimeRange, formatEventDateRange, formatLink, formatTarget, sliceStyle, groupEventsByDate } from '@/app/lib/utils';
import { parseISO, format, isSameDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { BackgroundImage } from "@/app/lib/ParallaxBackground";
import ScheduleFilters from '@/app/lib/ScheduleFilters';
import { MoreLink } from '@/app/lib/MoreLink';
import { RevealTitle } from '@/app/lib/RevealTitle';
import Chevron from '@/app/assets/images/icon_chevron.svg';
import stylesMoreLink from '@/app/lib/MoreLink/moreLink.module.scss';
import styles from './fullSchedule.module.scss';
import sharedStyles from '@/app/shared.module.scss';

const dateToSlug = (displayDate) => {
	if (!displayDate || typeof displayDate !== 'string') return '';
	const monthDay = displayDate.split(' ').slice(1).join(' ');
	return (dashify(monthDay) || '').toLowerCase();
};

const venueToSlug = (name) => (name && typeof name === 'string' ? (dashify(name) || '').toLowerCase() : '');

// Path up to and including "schedule" (e.g. /events/123/schedule or /schedule)
const getScheduleBasePath = (pathname) => {
	if (!pathname || typeof pathname !== 'string') return '/schedule';
	const segments = pathname.split('/').filter(Boolean);
	const i = segments.indexOf('schedule');
	if (i >= 0) return '/' + segments.slice(0, i + 1).join('/');
	return pathname;
};

// Filter segments after the schedule base (e.g. /events/123/schedule/feb-25 -> ['feb-25'])
const getFilterSegmentsFromPath = (pathname) => {
	const base = getScheduleBasePath(pathname);
	if (pathname === base) return [];
	const rest = pathname.slice(base.length).replace(/^\//, '');
	return rest ? rest.split('/') : [];
};

// Resolve URL segments to { date, venue } using grouped events and venue list
const resolveSegmentsToFilters = (segments, allGroupedEvents, venueOptions) => {
	if (!Array.isArray(segments) || segments.length === 0) return { date: null, venue: null };
	let date = null;
	let venue = null;
	const first = segments[0];
	const second = segments[1];
	if (first && first !== 'all') {
		const group = allGroupedEvents.find((g) => g?.displayDate && dateToSlug(g.displayDate) === first);
		if (group) date = group.displayDate;
	}
	const venueSegment = first === 'all' ? second : second;
	if (venueSegment && venueOptions?.length) {
		const match = venueOptions.find((v) => venueToSlug(v) === venueSegment);
		if (match) venue = match;
	}
	return { date, venue };
};

const FullSchedule = ({ slice, pageData }) => {
	const pathname = usePathname();
	const scheduleFilterSegments = pageData?.data?.scheduleFilterSegments;

	const scheduleBasePath = useMemo(() => getScheduleBasePath(pathname || ''), [pathname]);

	const allGroupedEvents = useMemo(() => groupEventsByDate(pageData?.data?.event), [pageData?.data?.event]);

	// Venue options (same precedence as ScheduleFilters / display)
	const venueOptions = useMemo(() => {
		const events = pageData?.data?.event || [];
		const venues = new Set();
		events.forEach((event) => {
			const name = event?.venue?.data?.name
				|| event?.linked_session?.data?.location
				|| event?.location
				|| event?.linked_session?.data?.venue?.data?.name;
			if (name) venues.add(name);
		});
		return Array.from(venues).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
	}, [pageData?.data?.event]);

	// Initial filters: prefer server scheduleFilterSegments, fallback to parsing pathname (needed when landing via back button with cached pageData)
	const resolvedFiltersFromSegments = useMemo(
		() => resolveSegmentsToFilters(scheduleFilterSegments, allGroupedEvents, venueOptions),
		[scheduleFilterSegments, allGroupedEvents, venueOptions]
	);
	const [filters, setFilters] = useState(resolvedFiltersFromSegments);
	// Bump when we update URL in handleFilterChange so the sync effect runs and reads window.location (pathname lags after replaceState)
	const [urlSyncKey, setUrlSyncKey] = useState(0);

	// Sync filters from the actual URL (window.location so clearing filters is reflected; pathname from router lags after replaceState)
	useEffect(() => {
		const pathToRead = typeof window !== 'undefined' ? window.location.pathname : pathname || '';
		const base = getScheduleBasePath(pathToRead);
		if (!pathToRead.startsWith(base)) return;
		const segmentsInPath = getFilterSegmentsFromPath(pathToRead);
		if (segmentsInPath.length > 0) {
			setFilters(resolveSegmentsToFilters(segmentsInPath, allGroupedEvents, venueOptions));
		} else {
			setFilters({ date: null, venue: null });
		}
	}, [pathname, urlSyncKey, allGroupedEvents, venueOptions]);

	// On mount and on popstate: sync from window.location (pathname can lag after back, so read URL directly)
	useEffect(() => {
		const syncFromUrl = () => {
			if (typeof window === 'undefined') return;
			const path = window.location.pathname;
			const base = getScheduleBasePath(path);
			if (!path.startsWith(base)) return;
			const segments = getFilterSegmentsFromPath(path);
			setFilters(resolveSegmentsToFilters(segments, allGroupedEvents, venueOptions));
		};

		syncFromUrl(); // run on mount so back-button landing picks up URL even if pathname hasn't updated yet

		window.addEventListener('popstate', syncFromUrl);
		return () => window.removeEventListener('popstate', syncFromUrl);
	}, [allGroupedEvents, venueOptions]);

	// Helper function to get the best available start date/time (prefers datetime, falls back to date)
	const getStartDateTime = (event) => {
		// Check linked_session first, then event
		const linkedSession = event?.linked_session?.data;
		if (isFilled.timestamp(linkedSession?.start_date_and_time)) {
			return linkedSession.start_date_and_time;
		}
		if (isFilled.timestamp(event?.start_date_and_time)) {
			return event.start_date_and_time;
		}
		if (isFilled.date(linkedSession?.start_date)) {
			return linkedSession.start_date;
		}
		if (isFilled.date(event?.start_date)) {
			return event.start_date;
		}
		return null;
	};

	// Helper function to get the best available end date/time (prefers datetime, falls back to date)
	const getEndDateTime = (event) => {
		const linkedSession = event?.linked_session?.data;
		if (isFilled.timestamp(linkedSession?.end_date_and_time)) {
			return linkedSession.end_date_and_time;
		}
		if (isFilled.timestamp(event?.end_date_and_time)) {
			return event.end_date_and_time;
		}
		if (isFilled.date(linkedSession?.end_date)) {
			return linkedSession.end_date;
		}
		if (isFilled.date(event?.end_date)) {
			return event.end_date;
		}
		return null;
	};

	// Helper function to check if a datetime field has time component
	const hasTimeComponent = (event, fieldName) => {
		const linkedSession = event?.linked_session?.data;
		return isFilled.timestamp(linkedSession?.[fieldName]) || isFilled.timestamp(event?.[fieldName]);
	};

	const filteredFlatEvents = useMemo(() => {
		const { date, venue } = filters;
		let events = pageData?.data?.event || [];

		return events.filter(event => {
			// if (event?.only_show_on_full_list) return false;

			if (date) {
				const startDateTime = getStartDateTime(event);
				if (startDateTime) {
					const eventDateString = format(parseISO(startDateTime), 'EEEE MMM d');
					if (eventDateString !== date) {
						return false;
					}
				}
			}

			if (venue) {
				// Same precedence as display and filter dropdown so filter matches what’s shown
				const venueName = event?.venue?.data?.name
					|| event?.linked_session?.data?.location
					|| event?.location
					|| event?.linked_session?.data?.venue?.data?.name;
				if (venueName !== venue) {
					return false;
				}
			}
			return true;
		});
	}, [pageData?.data?.event, filters]);

	const handleFilterChange = useCallback((newFilters) => {
		setFilters(newFilters);
		// Append filter segments to current schedule URL (e.g. /events/123/schedule/feb-25/wythe-hotel)
		const { date, venue } = newFilters;
		const dateSlug = date ? dateToSlug(date) : null;
		const venueSlug = venue ? venueToSlug(venue) : null;
		let path = scheduleBasePath;
		if (dateSlug) path += `/${dateSlug}`;
		if (venueSlug) path += dateSlug ? `/${venueSlug}` : `/all/${venueSlug}`;
		// Update browser URL via History API (no full navigation; back/forward handled by popstate below)
		if (typeof window !== 'undefined') {
			if (path !== scheduleBasePath) {
				window.history.pushState(null, '', path);
			} else {
				window.history.replaceState(null, '', scheduleBasePath);
			}
			setUrlSyncKey((k) => k + 1); // so sync effect runs and reads window.location (pathname lags)
		}
	}, [scheduleBasePath]);

	return (
		<BackgroundImage slicePrimary={slice?.primary}>
			<div className={clsx(styles.full_schedule, pageData?.data?.isSchedulePage && styles.isSchedulePage)} style={sliceStyle(slice?.primary)}>

				{/* title */}
				<div className={clsx(sharedStyles.top)}>
					<div className={clsx(sharedStyles.left)}>
						{isFilled.keyText(slice?.primary?.title) && (
							<h1 className={clsx(sharedStyles.h1)} id={dashify(slice?.primary?.title)}>
								<RevealTitle size='small'>{slice?.primary?.title}</RevealTitle>
							</h1>
						)}

						{pageData?.data?.links?.length > 0 && (
							<div className={clsx(styles.links)}>
								{pageData.data.links.map((item, index) => isFilled.link(item) ? (
									<MoreLink
										key={index}
										link={item}
										extraClass={clsx(stylesMoreLink.fuchsia_background)}
										keepOnMobile={true}
										showChevron={false}
									/>
								) : null)}
							</div>
						)}

					</div>

					{(slice?.primary?.show_filters && (allGroupedEvents.length > 0)) && (
						<ScheduleFilters
							groupedEvents={allGroupedEvents}
							allEvents={pageData?.data?.event}
							onFilterChange={handleFilterChange}
							selectedDate={filters.date}
							selectedVenue={filters.venue}
						/>
					)}

				</div>

				{/* schedule */}
				{filteredFlatEvents.length > 0 ? (
					<div className={clsx(styles.events)}>
						{filteredFlatEvents.map((event, index) => {

							// Tickets link/ID determination
							const ticketsLink = event?.linked_session?.data?.tickets_link || event?.buy_tickets_link;
							const ticketsSquadUpId = event?.linked_session?.data?.squadup_event_id || event?.squadup_event_id;

							const hasTicketsAction = isFilled.link(ticketsLink) || isFilled.keyText(ticketsSquadUpId);

							return (
								<div className={clsx(styles.event,
									event?.highlighted_event ? styles.fullWidth : null,
									isFilled.image(event?.linked_session?.data?.image?.['Schedule']) ? styles.hasImage : null,
									isFilled.image(event?.image) ? styles.hasImage : null
								)} key={index}>

									{(event?.highlighted_event && isFilled.image(event?.linked_session?.data?.image?.['Schedule'])) ? (
										<div className={clsx(styles.image_wrapper)}>
											<PrismicNextImage field={event?.linked_session?.data?.image?.['Schedule']} className={clsx(styles.image)} fallbackAlt='' />
										</div>
									) : (event?.highlighted_event && isFilled.image(event?.image)) ? (
										<div className={clsx(styles.image_wrapper)}>
											<PrismicNextImage field={event?.image} className={clsx(styles.image)} fallbackAlt='' />
										</div>
									) : null}

									<div className={clsx(styles.details)}>
										{(() => {
											const startDateTime = getStartDateTime(event);
											const endDateTime = getEndDateTime(event);

											if (!startDateTime && !endDateTime) return null;

											// Determine if we have time components (datetime fields)
											const hasStartTime = hasTimeComponent(event, 'start_date_and_time');
											const hasEndTime = hasTimeComponent(event, 'end_date_and_time');

											// Format date/day part - check if we have both dates and if they're different
											let dayPart = null;
											if (startDateTime && endDateTime) {
												const startDate = parseISO(startDateTime);
												const endDate = parseISO(endDateTime);
												if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
													if (!isSameDay(startDate, endDate)) {
														// Different days - show date range
														dayPart = formatEventDateRange(startDateTime, endDateTime);
													} else {
														// Same day - show just the day
														dayPart = formatEventStartDay(startDateTime);
													}
												} else {
													dayPart = formatEventStartDay(startDateTime || endDateTime);
												}
											} else {
												// Only one date available
												dayPart = formatEventStartDay(startDateTime || endDateTime);
											}

											// Format time part - only if we have at least one time component
											let timePart = null;
											if (hasStartTime || hasEndTime) {
												// Get the actual datetime values (not date-only) for time formatting
												const linkedSession = event?.linked_session?.data;
												const startForTime = hasStartTime
													? (linkedSession?.start_date_and_time || event?.start_date_and_time)
													: undefined;
												const endForTime = hasEndTime
													? (linkedSession?.end_date_and_time || event?.end_date_and_time)
													: undefined;

												// formatEventTimeRange requires a start time, so handle cases where we only have end time
												if (hasStartTime) {
													timePart = formatEventTimeRange(startForTime, endForTime);
												} else if (hasEndTime) {
													// Only end time available - format just the end time
													const endTime = parseISO(endForTime);
													if (!isNaN(endTime.getTime())) {
														const EST_TIMEZONE = 'America/New_York';
														timePart = formatInTimeZone(endTime, EST_TIMEZONE, 'h:mm a');
													}
												}
											}

											return (
												<div className={clsx(styles.head)}>
													{dayPart && (
														<div className={clsx(styles.dayPartWrapper)}>
															<span className={clsx(styles.dayPart)}>{dayPart}</span>
															{timePart && <><span className={clsx(styles.timePart)}>{timePart}</span></>}
														</div>
													)}
													<div className={clsx(styles.locationWrapper)}>
														{/* Event venue name has precedence over linked session location when filled in */}
														{event?.venue?.data?.name ? (<span className={clsx(styles.location)}>{event.venue.data.name}</span>) :
															event?.linked_session?.data?.location ? (<span className={clsx(styles.location)}>{event.linked_session.data.location}</span>) :
																event?.location ? (<span className={clsx(styles.location)}>{event.location}</span>) : null}
														{event?.linked_session?.data?.room ? (<span className={clsx(styles.room)}>{event?.linked_session?.data?.room}</span>) :
															event?.room ? (<span className={clsx(styles.room)}>{event.room}</span>) : null}
													</div>
												</div>
											);
										})()}

										<div className={clsx(styles.body)}>
											{/* Clickable title: link to linked_session or more_info_link when we have a valid URL */}
											{(() => {
												const hasLinkedSession = isFilled.contentRelationship(event?.linked_session);
												const hasMoreInfoLink = isFilled.link(event?.more_info_link);
												const titleFromSession = event?.linked_session?.data?.title;
												const titleFromEvent = event?.title;
												const title = isFilled.keyText(titleFromSession) ? titleFromSession : (isFilled.keyText(titleFromEvent) ? titleFromEvent : null);

												if (!title) return null;

												const hrefFromSession = hasLinkedSession ? formatLink(event?.linked_session) : null;
												const hrefFromMoreInfo = hasMoreInfoLink ? formatLink(event?.more_info_link) : null;
												const href = (hrefFromSession && hrefFromSession !== '#') ? hrefFromSession : (hrefFromMoreInfo && hrefFromMoreInfo !== '#') ? hrefFromMoreInfo : null;

												return (
													<ConditionalLink url={href} condition={!!href} className={clsx(styles.link)}>
														<h3 className={clsx(styles.title)}>{title}</h3>
													</ConditionalLink>
												);
											})()}

											{isFilled.richText(event?.description) ? (
												<RichText field={event.description} />
											) : isFilled.richText(event?.linked_session?.data?.introduction) ? (
												<RichText field={event?.linked_session?.data?.introduction} />
											) : ''}
										</div>

										{(isFilled.contentRelationship(event?.linked_session) || isFilled.link(event?.more_info_link) || hasTicketsAction) && (
											<div className={clsx(styles.footer)}>

												{/* More Info Link (Uses standard Link, NO ConditionalLink/squadup_event_id) */}
												{isFilled.contentRelationship(event?.linked_session) ? (
													<Link href={formatLink(event?.linked_session)} className={clsx(styles.link)}>
														<span className={clsx(styles.inner)}>{event?.more_info?.text || "More info"}</span>
														<Chevron className={clsx(styles.chevron)} />
													</Link>
												) : isFilled.link(event?.more_info_link) ? (
													<Link href={formatLink(event?.more_info_link)} className={clsx(styles.link)}>
														<span className={clsx(styles.inner)}>{event?.more_info?.text || "More info"}</span>
														<Chevron className={clsx(styles.chevron)} />
													</Link>
												) : null}

												{/* Tickets Link (Uses ConditionalLink to inject squadup_event_id if present) */}
												{hasTicketsAction ? (
													<MoreLink
														{...(ticketsSquadUpId ? {} : { link: ticketsLink })}
														text={event?.linked_session?.data?.tickets_link?.text || event?.buy_tickets_link?.text || "Tickets"}
														extraClass={clsx(stylesMoreLink.fuchsia_background, 'ms-auto')}
														keepOnMobile={true}
														squadupeventid={ticketsSquadUpId} />
												) : null}
											</div>
										)}

									</div>
								</div>
							);
						})}
					</div>
				) : (
					<p className={styles.noEvents}>No events match the current filter.</p>
				)}

				{/* back link */}
				{pageData?.type === 'event' && (
					<MoreLink
						link={`/${Settings.eventsPrefix}/${pageData?.uid}`}
						text={"Back to event"}
						slice={slice}
						back={true}
					/>
				)}

			</div>
		</BackgroundImage>
	);
}

export default FullSchedule;